import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { useOverlayDismiss, OVERLAY_PANEL_PROPS } from "@shared-ui/hooks";
import type React from "react";

/** つまみをこれより下へ引いて離したら閉じる（px）。シートが低いときはその高さの 1/3。 */
const PULL_CLOSE_PX = 120;

interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  /** シートの最大高 (vh)。デフォルト 80 */
  maxHeightVh?: number;
  /** 背景オーバーレイをタップしたら閉じる。デフォルト true */
  closeOnBackdropClick?: boolean;
  /**
   * PRODUCT_DEV-631: 重ね順の Tailwind クラス。デフォルト "z-50"。
   * メニュー（MenuSheet / AdminLayout）自体が z-50 のため、その上に開くシートは
   * "z-[60]" 等を渡さないと背面に隠れる（MobileTabBar に同種の z-50 衝突の既往あり）。
   */
  zIndexClass?: string;
  /** 保存中・送信中。✕・外側・Esc のどれでも閉じない（✕ は押せなくなる） */
  busy?: boolean;
}

/**
 * PRODUCT_DEV-414 follow-up #3: 下から立ち上がる bottom sheet 共通コンポーネント。
 * CopyPartnerSelect の `animate-slideUpFromBottom` パターンを共通化し、Preset 等
 * 各種モーダルでスライドアップモーションを統一する。
 *
 * PRODUCT_DEV-907: 中央のモーダル（`ModalShell`）と同じく**右上に ✕ を必ず出す**。
 * 各画面で ✕ を書かない（書くと二重になる）。見出しは ✕ の下に潜らないよう `pr-10` を付ける。
 * 例外は「ドック」と名の付くもの（記録ドックなど）だけで、それらはこの部品を使わない。
 *
 * PRODUCT_DEV-1059: 上のつまみを下へ引いて閉じられる（引いている間はシートが指についてくる）。
 * 閉じてよいのは**外側を押して閉じられるときだけ**（`closeOnBackdropClick` かつ `busy` でない）。
 * 入力の途中で外側を押しても閉じない画面は、引いても閉じない（PRODUCT_DEV-907 と同じ）。
 * Pointer Events なので指（Android・iPhone）でもマウス（PC）でも同じに動く。
 */
export const BottomSheet: React.FC<BottomSheetProps> = ({
  isOpen,
  onClose,
  children,
  maxHeightVh = 80,
  closeOnBackdropClick = true,
  zIndexClass = "z-50",
  busy = false,
}) => {
  /*
    外側を押して閉じる規則は**共通部品**（PD-710。`@shared-ui/hooks`）。
    以前はオーバーレイの `onClick` で閉じており、**中で文字をドラッグ選択して
    外側で指を離すと閉じて**いた（click は mousedown と mouseup の共通祖先で
    発火するので、パネルの `stopPropagation` を通らない）。
    押した位置と離した位置の**両方がパネルの外**のときだけ閉じる。
  */
  const overlayDismiss = useOverlayDismiss(
    closeOnBackdropClick && !busy ? onClose : undefined,
  );
  // Esc キーで閉じる
  useEffect(() => {
    if (!isOpen || busy) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, busy, onClose]);

  // ---- つまみを下へ引いて閉じる ----
  const canPull = closeOnBackdropClick && !busy;
  const [pull, setPull] = useState(0);
  const [pulling, setPulling] = useState(false);
  const pullFrom = useRef<number | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const endPull = (close: boolean) => {
    pullFrom.current = null;
    setPulling(false);
    const height = panelRef.current?.offsetHeight || 0;
    const limit = height > 0 ? Math.min(PULL_CLOSE_PX, height / 3) : PULL_CLOSE_PX;
    // 閉じるときも 0 に戻す（開き直したとき、前に引いた位置から出てこないように）
    setPull(0);
    if (close && pull > limit) onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 bg-black bg-opacity-50 ${zIndexClass} animate-fadeIn`}
      {...overlayDismiss}
    >
      {/* 引いた分だけ下げる箱。パネル自身は出てくるアニメーション（fill-mode: both）が transform を
          持ち続けるので、そこに書いても効かない。外の箱を動かす */}
      <div
        className={`absolute bottom-0 left-0 right-0 ${pulling ? "" : "transition-transform duration-200"}`}
        style={pull > 0 ? { transform: `translateY(${pull}px)` } : undefined}
      >
      <div
        ref={panelRef}
        className="relative flex flex-col bg-white rounded-t-3xl pb-safe-bottom animate-slideUpFromBottom"
        style={{ maxHeight: `${maxHeightVh}vh` }}
        // **この印が無いとパネルの中を押しても閉じる**（判定は「パネルの外か」で行う）。
        {...OVERLAY_PANEL_PROPS}
        role="dialog"
        aria-modal="true"
      >
        {/* つまみ。上の帯を下へ引くと閉じる。右の 80px は空ける（✕ と、詳細シートの鉛筆を押せるように）。
            閉じられない間は出さない（引けそうに見えて動かないのを避ける） */}
        {canPull && (
          <div
            aria-hidden="true"
            data-testid="sheet-grabber"
            className="absolute top-0 left-0 right-20 h-5 z-10 flex justify-center pt-2 touch-none cursor-grab"
            onPointerDown={(e) => {
              if (e.pointerType === "mouse" && e.button !== 0) return;
              pullFrom.current = e.clientY;
              e.currentTarget.setPointerCapture?.(e.pointerId);
            }}
            onPointerMove={(e) => {
              if (pullFrom.current === null) return;
              setPulling(true);
              setPull(Math.max(0, e.clientY - pullFrom.current));
            }}
            onPointerUp={() => pullFrom.current !== null && endPull(true)}
            onPointerCancel={() => pullFrom.current !== null && endPull(false)}
          >
            <span className="block w-10 h-1 rounded-full bg-neutral-200 ml-20" />
          </div>
        )}
        {/* ✕ はスクロールする中身の外に置く（中身が長いとき一緒に流れて見えなくならないように） */}
        <button
          type="button"
          onClick={onClose}
          disabled={busy}
          aria-label="閉じる"
          className="absolute top-4 right-4 z-10 p-1 bg-white hover:bg-neutral-100 rounded-full transition-colors disabled:opacity-[.45] disabled:cursor-not-allowed"
        >
          <X size={20} className="text-neutral-600" />
        </button>
        {/* PRODUCT_DEV-1091: 縦のスクロールだけにする。overflow-y を auto にすると横も auto になり、
            中身が横にはみ出すとシートの中が横スクロールになっていた */}
        <div className="min-h-0 overflow-y-auto overflow-x-hidden">{children}</div>
      </div>
      </div>
    </div>
  );
};

export default BottomSheet;
